import { isLayoutUuid } from './layoutLinkUtils';
import { resolveFloorPlanPreviewUrl } from './floorPlanPreviewUrl';

const pickCopiedFrom = (row) =>
  row?.baseCopiedFrom || row?.copiedFrom || row?.copiedFromLayout || null;

/** Client copy / fork — has an explicit copied-from edge (not session parent alone). */
export const isCopyForkLayout = (node) => Boolean(node?.copiedFromId);

/** Mongo _id or populated ref object → string id. */
const resolveIdRef = (ref) => {
  if (ref == null || ref === '') return null;
  if (typeof ref === 'string') return ref.trim() || null;
  if (typeof ref === 'object' && ref._id != null) return String(ref._id);
  return null;
};

// Populated user ref → minimal { id, name, email } for row attribution.
// Returns null when no usable identity is present.
const pickAuthor = (row) => {
  const u = row?.user;
  if (!u || typeof u !== 'object') return null;
  const id = u._id != null ? String(u._id) : null;
  const name = typeof u.name === 'string' ? u.name.trim() : '';
  const email = typeof u.email === 'string' ? u.email.trim() : '';
  if (!id && !name && !email) return null;
  return { id, name: name || null, email: email || null };
};

const pickResolvedPreviewUrl = (...candidates) => {
  for (const candidate of candidates) {
    const url = resolveFloorPlanPreviewUrl(candidate);
    if (url) return url;
  }
  return null;
};

/**
 * Normalize a getByPartner layout row for the floor plan library.
 */
export function normalizePartnerLayout(row) {
  if (!row) return null;
  const parentRef = row.parent;
  const parentId = resolveIdRef(parentRef);
  const copiedFrom = pickCopiedFrom(row);
  const copiedFromId = resolveIdRef(copiedFrom);

  return {
    _id: row._id ? String(row._id) : null,
    uuid: row.uuid && isLayoutUuid(row.uuid) ? String(row.uuid).trim() : null,
    name: row.name || 'Floor plan',
    createdAt: row.createdAt,
    furnishedThumbnail: pickResolvedPreviewUrl(
      row.thumbnailfurnishedImageId,
      row.furnishedImageId,
      row.furnishedThumbnail
    ),
    thumbnail: pickResolvedPreviewUrl(
      row.thumbnailImage,
      row.thumbnailfurnishedImageId,
      row.furnishedImageId,
      row.image,
      row.thumbnail
    ),
    // Unfurnished / blank upload — preferred for analytics hero previews.
    blankImage: pickResolvedPreviewUrl(row.image, row.thumbnailImage, row.thumbnail),
    rootLayoutUuid: row.rootLayoutUuid || null,
    parentId,
    copiedFromId,
    galleryUrl:
      typeof row.galleryUrl === 'string' && /^https?:\/\//i.test(row.galleryUrl.trim())
        ? row.galleryUrl.trim()
        : null,
    copiedFromName:
      (typeof copiedFrom === 'object' && copiedFrom?.name) || row.copiedFromName || null,
    author: pickAuthor(row),
    isFork: Boolean(copiedFromId),
    lifetimeViews:
      (Number(row.totalGalleryViews) || 0) + (Number(row.totalEditorViews) || 0),
    lifetimeShares: Number(row.totalShares) || 0,
    lifetimeDownloads: Number(row.totalDownloads) || 0,
    lifetimeShuffles: Number(row.totalShuffles) || 0,
  };
}

/**
 * Depth-first flatten of getByPartner payloads. The API returns copies nested
 * under each root in `children[]` (see PartnersLayout.filterLayouts).
 */
export function flattenPartnerLayoutsFromApi(layouts) {
  const out = [];
  const seen = new Set();
  const walk = (item) => {
    if (!item?._id) return;
    const id = String(item._id);
    if (seen.has(id)) return;
    seen.add(id);
    out.push(item);
    const kids = Array.isArray(item.children) ? item.children : [];
    kids.forEach((child) => {
      if (typeof child === 'string') return;
      walk(child);
    });
  };
  (layouts || []).forEach(walk);
  return out;
}

/**
 * Walk the ancestor chain of `ancestorId` looking for `descendantId`.
 */
function chainContains(nodesMap, ancestorId, descendantId) {
  let cursor = nodesMap.get(ancestorId);
  const seen = new Set();
  while (cursor) {
    if (cursor._id === descendantId) return true;
    if (seen.has(cursor._id)) return false;
    seen.add(cursor._id);
    const parentKey = cursor.copiedFromId || cursor.parentId;
    cursor = parentKey ? nodesMap.get(parentKey) : null;
  }
  return false;
}

function sortTreeByDate(list) {
  list.sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  );
  list.forEach((n) => {
    if (n.children?.length) sortTreeByDate(n.children);
  });
}

function subtreeContains(node, targetId) {
  if (!node || !targetId) return false;
  return (node.children || []).some(
    (child) => child._id === targetId || subtreeContains(child, targetId)
  );
}

function resolveParentKey(node, nodeById) {
  const directParent = node.copiedFromId || node.parentId;
  if (!directParent) return null;
  if (nodeById.has(directParent)) return directParent;
  // Broken fork ref — climb to the lineage root when the direct copy isn't loaded.
  if (node.rootLayoutUuid) {
    for (const candidate of nodeById.values()) {
      if (candidate.uuid === node.rootLayoutUuid && candidate._id !== node._id) {
        if (subtreeContains(node, candidate._id)) continue;
        return candidate._id;
      }
    }
  }
  return directParent;
}

/**
 * Display-only tree rules (catalog fetch unchanged):
 *   • Copies never get a top-level row — only agent uploads are roots.
 *   • Nested copies with a missing direct parent are omitted.
 *   • When a fork would be the only top-level row, hoist its non-fork
 *     children to the root list so pure uploads aren't silently dropped.
 */
export function filterDisplayTreeNode(node, atRoot = false) {
  if (atRoot && isCopyForkLayout(node)) return null;
  if (!atRoot && isCopyForkLayout(node) && node?.parentMissing) return null;
  const children = (node?.children || [])
    .map((child) => filterDisplayTreeNode(child, false))
    .filter(Boolean);
  return { ...node, children };
}

/** Walk a fork branch and promote non-fork descendants to top-level display roots. */
function collectHoistedFromForkBranch(node) {
  const out = [];
  (node?.children || []).forEach((child) => {
    if (isCopyForkLayout(child) && child?.parentMissing) return;
    if (isCopyForkLayout(child)) {
      out.push(...collectHoistedFromForkBranch(child));
      return;
    }
    const filtered = filterDisplayTreeNode(child, true);
    if (filtered) out.push(filtered);
  });
  return out;
}

export function filterDisplayTreeRoots(roots) {
  const out = [];
  (roots || []).forEach((node) => {
    if (isCopyForkLayout(node)) {
      out.push(...collectHoistedFromForkBranch(node));
      return;
    }
    const filtered = filterDisplayTreeNode(node, true);
    if (filtered) out.push(filtered);
  });
  return out;
}

/**
 * Build a parent→children tree from getByPartner roots.
 *
 * Pass 1 — honor API `children[]` (authoritative for client copies).
 * Pass 2 — attach remaining flat nodes via copiedFrom / parent / rootLayoutUuid.
 *          When the direct ancestor isn't in this response, fall back to
 *          climbing rootLayoutUuid so the descendant nests under its
 *          lineage root and the root keeps its "has copies" indicator.
 */
export function buildPartnerLayoutTree(apiRoots) {
  const flat = flattenPartnerLayoutsFromApi(apiRoots);
  const nodeById = new Map();

  flat.forEach((raw) => {
    const n = normalizePartnerLayout(raw);
    if (n?._id) {
      nodeById.set(n._id, {
        ...n,
        children: [],
        parentMissing: false,
        missingParentId: null,
      });
    }
  });

  const placed = new Set();

  const attachApiChildren = (raw, node) => {
    (Array.isArray(raw.children) ? raw.children : []).forEach((childRaw) => {
      const childId = resolveIdRef(childRaw);
      if (!childId || !nodeById.has(childId) || placed.has(childId)) return;
      const childNode = nodeById.get(childId);
      node.children.push(childNode);
      placed.add(childId);
      attachApiChildren(childRaw, childNode);
    });
    if (node.children.length) sortTreeByDate(node.children);
  };

  (apiRoots || []).forEach((raw) => {
    const id = raw?._id ? String(raw._id) : null;
    if (!id || !nodeById.has(id)) return;
    attachApiChildren(raw, nodeById.get(id));
  });

  const findRootByUuid = (uuid, selfId) => {
    if (!uuid) return null;
    for (const candidate of nodeById.values()) {
      if (candidate.uuid === uuid && candidate._id !== selfId) return candidate;
    }
    return null;
  };

  const roots = [];
  const unplaced = [...nodeById.values()].filter((n) => !placed.has(n._id));
  unplaced.forEach((node) => {
    const parentKey = resolveParentKey(node, nodeById);
    const parentExists = parentKey && nodeById.has(parentKey);
    const wouldCycle = parentExists && chainContains(nodeById, parentKey, node._id);
    if (parentExists && !wouldCycle) {
      const parent = nodeById.get(parentKey);
      if (!parent.children.some((c) => c._id === node._id)) {
        parent.children.push(node);
      }
      placed.add(node._id);
      return;
    }
    // Direct ancestor isn't in this response (common when the API prefers
    // returning own-owned descendants over the cross-partner copy that sits
    // between them, e.g. partner forks a sub-user's plan → the sub-user's
    // copy gets pruned in favor of the partner's new copy). If the row
    // carries a rootLayoutUuid, climb to that lineage root so the descendant
    // still nests visually under its origin. Without this fallback the
    // origin's "X copies" indicator silently disappears whenever an
    // intermediate generation drops out of the page.
    if (parentKey && !parentExists) {
      const rootAncestor = findRootByUuid(node.rootLayoutUuid, node._id);
      const wouldCycleViaRoot =
        rootAncestor && chainContains(nodeById, rootAncestor._id, node._id);
      const wouldInvert =
        rootAncestor && subtreeContains(node, rootAncestor._id);
      if (rootAncestor && !wouldCycleViaRoot && !wouldInvert) {
        if (!rootAncestor.children.some((c) => c._id === node._id)) {
          rootAncestor.children.push(node);
        }
        node.parentMissing = true;
        node.missingParentId = parentKey;
        placed.add(node._id);
        return;
      }
      node.parentMissing = true;
      node.missingParentId = parentKey;
    }
    roots.push(node);
    placed.add(node._id);
  });

  sortTreeByDate(roots);
  return roots;
}

/** @deprecated Use buildPartnerLayoutTree — kept for imports/tests. */
export function buildForkTreeRoots(apiRoots) {
  return buildPartnerLayoutTree(apiRoots);
}

/** Collect ids of nodes that have children (default-expanded in the library UI). */
export function collectExpandableNodeIds(roots) {
  const ids = new Set();
  const walk = (node) => {
    if (node?.children?.length) {
      ids.add(node._id);
      node.children.forEach(walk);
    }
  };
  (roots || []).forEach(walk);
  return ids;
}

/**
 * Maps for feed ↔ catalog identity (ObjectId keys from activity feed).
 */
export function buildLayoutIdentityMaps(flatLayouts) {
  const uuidByMongoId = new Map();
  const mongoIdByUuid = new Map();
  const flat = Array.isArray(flatLayouts?.[0]?.children)
    ? flattenPartnerLayoutsFromApi(flatLayouts)
    : flatLayouts || [];
  flat.forEach((raw) => {
    const n = normalizePartnerLayout(raw);
    if (!n?._id) return;
    if (n.uuid) {
      uuidByMongoId.set(n._id, n.uuid);
      mongoIdByUuid.set(n.uuid, n._id);
    }
  });
  return { uuidByMongoId, mongoIdByUuid };
}
